import { useState } from 'react';

interface LrnConfig {
  status?: string;
  useCallback: boolean;
  callbackDelay: number;
  isOverrideEnabled: boolean;
}

interface LrnGroup {
  lrn: string;
  sequences: string[];
  currentStatus?: string;
}

interface UseSimulatorConfigProps {
  lrnGroups?: LrnGroup[];
  onSuccess: () => void;
  mutateFn: any;
}

export const useSimulatorConfig = ({ lrnGroups, onSuccess, mutateFn }: UseSimulatorConfigProps) => {
  const initialConfig: LrnConfig = {
    status: undefined,
    useCallback: true,
    callbackDelay: 1000,
    isOverrideEnabled: false,
  };
  const [lrnConfigs, setLrnConfigs] = useState<Record<string, LrnConfig>>({});
  const [applyAllConfig, setApplyAllConfig] = useState<LrnConfig>(initialConfig);

  const handleApplyToAll = () => {
    const nextConfigs: Record<string, LrnConfig> = {};
    lrnGroups?.forEach((group) => {
      nextConfigs[group.lrn] = { ...applyAllConfig, isOverrideEnabled: false };
    });
    setLrnConfigs(nextConfigs);
  };

  const handleConfirmChanges = async () => {
    const changedLrns =
      lrnGroups
        ?.map((group) => {
          const config = lrnConfigs[group.lrn];
          const effectiveConfig = config?.isOverrideEnabled ? config : applyAllConfig;

          if (!effectiveConfig.status?.trim()) {
            return null;
          }

          return {
            lrn: group.lrn,
            status: effectiveConfig.status,
            useCallback: effectiveConfig.useCallback,
            callbackDelay: effectiveConfig.callbackDelay,
          };
        })
        .filter(Boolean)
        .map((item) => item as NonNullable<typeof item>) || [];

    if (changedLrns.length === 0) return;
    await mutateFn({ body: { lrns: changedLrns } });
    onSuccess();
  };

  const updateLrnConfig = (lrn: string, updates: Partial<LrnConfig>) => {
    setLrnConfigs((prev) => ({
      ...prev,
      [lrn]: {
        ...initialConfig,
        ...applyAllConfig,
        ...prev[lrn],
        ...updates,
      },
    }));
  };

  const resetApplyAllConfig = () => {
    setApplyAllConfig(initialConfig);
  };

  const resetLrnConfig = (lrn: string) => {
    setLrnConfigs((prev) => ({
      ...prev,
      [lrn]: {
        ...applyAllConfig,
        isOverrideEnabled: false,
      },
    }));
  };

  const hasChanges = !!applyAllConfig.status?.trim() || Object.values(lrnConfigs).some((config) => config.status?.trim());

  return {
    lrnConfigs,
    applyAllConfig,
    setApplyAllConfig,
    handleApplyToAll,
    handleConfirmChanges,
    updateLrnConfig,
    hasChanges,
    resetApplyAllConfig,
    resetLrnConfig,
    initialConfig,
  };
};
